import { create } from 'zustand';
import {
  SIN_PESTANAS,
  abrir,
  activar,
  cerrar,
  leer,
  llaveDeGuardado,
  paraGuardar,
  renombrar,
  type EstadoDePestanas,
  type Pestana,
} from '@/lib/pestanas';
import { useAuthStore } from './auth.store';
import { useWorkspaceStore } from './workspace.store';

/**
 * LAS PESTAÑAS ABIERTAS. La lógica es de `lib/pestanas.ts`; aquí solo se guarda
 * el estado y se conecta con el navegador.
 *
 * Viven en `sessionStorage`, con una llave por usuario y negocio: sobreviven a recargar y no
 * pasan a otra cuenta. Al cerrar sesión o cambiar de negocio se vacían (y se borra lo guardado
 * de la cuenta que se deja).
 */
interface PestanasState extends EstadoDePestanas {
  abrirPestana: (p: Omit<Pestana, 'clave'>) => void;
  cerrarPestana: (clave: string) => void;
  /** `null` vuelve a la pantalla; las de detalle siguen abiertas. */
  activarPestana: (clave: string | null) => void;
  renombrarPestana: (clave: string, titulo: string) => void;
}

/** Un navegador con el almacenamiento bloqueado (o una prueba, sin navegador) no rompe nada. */
function almacen(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}
const llaveDeAhora = () => llaveDeGuardado(useAuthStore.getState().user?.id, useWorkspaceStore.getState().activeId);

let llave = llaveDeAhora();

export const usePestanas = create<PestanasState>()((set) => ({
  ...leer(almacen(), llave),
  abrirPestana: (p) => set((s) => abrir(s, p)),
  cerrarPestana: (clave) => set((s) => cerrar(s, clave)),
  activarPestana: (clave) => set((s) => activar(s, clave)),
  renombrarPestana: (clave, titulo) => set((s) => renombrar(s, clave, titulo)),
}));

usePestanas.subscribe((s) => {
  if (!llave) return;
  try {
    almacen()?.setItem(llave, paraGuardar(s));
  } catch {
    // Sin sitio o sin permiso: las pestañas siguen en memoria hasta recargar.
  }
});

/** Otra cuenta u otro negocio: lo abierto era de la anterior, y no se le enseña a esta. */
function alCambiarDeCuenta(): void {
  const nueva = llaveDeAhora();
  if (nueva === llave) return;
  try {
    if (llave) almacen()?.removeItem(llave);
  } catch {
    // Igual que arriba.
  }
  llave = nueva;
  usePestanas.setState(llave ? leer(almacen(), llave) : SIN_PESTANAS);
}
useAuthStore.subscribe(alCambiarDeCuenta);
useWorkspaceStore.subscribe(alCambiarDeCuenta);

/** Para abrir desde cualquier sitio, también fuera de un componente. */
export const abrirPestana = (p: Omit<Pestana, 'clave'>): void => usePestanas.getState().abrirPestana(p);
